// User ID管理工具

const USER_ID_KEY = 'sleepcalc_user_id';

/**
 * 生成UUID v4（使用Web Crypto API）
 */
function generateUUID(): string {
  // 使用crypto.randomUUID()如果可用（现代浏览器）
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // 回退方案：手动生成UUID v4
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * 生成设备指纹（简单版本）
 * 基于浏览器特征生成相对稳定的标识符
 */
function generateDeviceFingerprint(): string {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Device fingerprint', 2, 2);
  }
  
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    new Date().getTimezoneOffset(),
    canvas.toDataURL()
  ].join('|');
  
  // 使用简单的哈希函数生成固定长度的ID
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // 转换为32位整数
  }
  
  return 'fp_' + Math.abs(hash).toString(36);
}

/**
 * 获取或创建用户ID
 * 优先使用localStorage中的ID，如果不存在则创建新的
 */
export function getUserId(): string {
  try {
    // 首先尝试从localStorage获取
    let userId = localStorage.getItem(USER_ID_KEY);
    
    if (userId && userId.trim()) {
      return userId;
    }
    
    // 如果localStorage中没有，生成新的UUID
    userId = 'user_' + generateUUID();
    
    // 保存到localStorage
    localStorage.setItem(USER_ID_KEY, userId);
    
    return userId;
  } catch (error) {
    console.warn('无法访问localStorage，使用设备指纹生成用户ID:', error);
    // 如果localStorage不可用，使用设备指纹
    return generateDeviceFingerprint();
  }
}

/**
 * 设置用户ID（用于测试或特殊场景）
 */
export function setUserId(userId: string): void {
  try {
    localStorage.setItem(USER_ID_KEY, userId);
  } catch (error) {
    console.warn('无法保存用户ID到localStorage:', error);
  }
}

/**
 * 清除用户ID（用于重置或测试）
 */
export function clearUserId(): void {
  try {
    localStorage.removeItem(USER_ID_KEY);
  } catch (error) {
    console.warn('无法清除localStorage中的用户ID:', error);
  }
}

/**
 * 验证用户ID格式
 */
export function isValidUserId(userId: string): boolean {
  if (!userId || typeof userId !== 'string') {
    return false;
  }
  
  // 检查是否为UUID格式或设备指纹格式
  const uuidPattern = /^user_[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
  const fingerprintPattern = /^fp_[0-9a-z]+$/;
  
  return uuidPattern.test(userId) || fingerprintPattern.test(userId);
}

/**
 * 获取用户ID的类型
 */
export function getUserIdType(userId: string): 'uuid' | 'fingerprint' | 'unknown' {
  if (userId.startsWith('user_')) {
    return 'uuid';
  } else if (userId.startsWith('fp_')) {
    return 'fingerprint';
  }
  return 'unknown';
}